import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, apiGet, apiGetList, apiPut } from './client';

/**
 * Marketing CMS seam (PHASE2 D10). Public read of the platform marketing content
 * map, and the platform-admin editor list/save. In mock mode the public read
 * returns `{}` so the marketing pages fall back to their built-in defaults.
 */

/** A section's content is a flat map of string fields (e.g. hero = {eyebrow, headline, subhead}). */
export type CmsSectionValue = Record<string, string>;
export type CmsMap = Record<string, CmsSectionValue>;

/** Public content map `{ sectionKey: { field: value } }`. */
export async function getCms(): Promise<CmsMap> {
  if (USE_MOCKS) return {};
  try {
    return await apiGet<CmsMap>('/cms');
  } catch {
    return {};
  }
}

export function useCms() {
  return useQuery({ queryKey: ['cms'], queryFn: getCms, staleTime: 60_000 });
}

/** Read a single field with a hardcoded fallback — the pattern every marketing page uses. */
export function cmsText(cms: CmsMap | undefined, section: string, field: string, fallback: string): string {
  const v = cms?.[section]?.[field];
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

// ── Admin editor ────────────────────────────────────────────────────────────
export interface AdminCmsSection {
  sectionKey: string;
  value: CmsSectionValue;
  updatedAt: string | null;
}

export function useAdminCms() {
  return useQuery({ queryKey: ['admin-cms'], queryFn: () => apiGetList<AdminCmsSection>('/admin/cms') });
}

export function saveCmsSection(sectionKey: string, value: CmsSectionValue) {
  return apiPut(`/admin/cms/${sectionKey}`, { value });
}
